<script setup lang="ts">
import Switch from '@payjib/ui/components/Switch.vue'
import { useField, useForm } from 'vee-validate'
import { watch } from 'vue'
import * as yup from 'yup'
import {
  toggleCurrencyAutoUpdateAction,
  updateCurrencyAction,
  updateCurrencyExchangeRateAction,
} from '@/api/functions/settings/get-currencies-action'
import { useMutationApi } from '@/composables/useApi'
import { isSuperAdmin } from '@/composables/useAuth'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

interface CurrencyFormValues {
  title: string
  iso: string
  selling_exchange_rate_margin: number | string | null
  buying_exchange_rate_margin: number | string | null
  auto_update_enabled: boolean
  exchange_rate: number | string | null
}
const props = defineProps({
  modelValue: Boolean,
  selectedCurrency: { type: Object, required: true },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])
const toast = useToast()
// const queryClient = useQueryClient();

// --- Validation Schema ---
const schema = yup.object({
  title: yup.string().required('نام ارز الزامی است').min(2).max(100),
  iso: yup.string().required('کد ارز الزامی است').length(3),
  selling_exchange_rate_margin: yup.number().required('بازه قیمتی فروش الزامی است').typeError('عدد وارد کنید'),
  buying_exchange_rate_margin: yup.number().required('بازه قیمتی خرید الزامی است').typeError('عدد وارد کنید'),
  auto_update_enabled: yup.boolean().default(false),
  exchange_rate: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('عدد وارد کنید')
    .when('auto_update_enabled', {
      is: false,
      then: (schema) => schema.required('نرخ ارز الزامی است').positive('نرخ باید بزرگتر از صفر باشد'),
    }),
})

// --- Form setup ---
const { handleSubmit, errors, resetForm } = useForm<CurrencyFormValues>({
  validationSchema: schema,
  initialValues: {
    title: '',
    iso: '',
    selling_exchange_rate_margin: null,
    buying_exchange_rate_margin: null,
    auto_update_enabled: false,
    exchange_rate: null,
  },
})

// Fields
const { value: title } = useField('title')
const { value: iso } = useField('iso')
const { value: selling_exchange_rate_margin } = useField('selling_exchange_rate_margin')
const { value: buying_exchange_rate_margin } = useField('buying_exchange_rate_margin')
const { value: auto_update_enabled } = useField<boolean>('auto_update_enabled')
const { value: exchange_rate } = useField('exchange_rate')
// --- Watch selectedCurrency and populate form ---
watch(
  () => props.selectedCurrency,
  (currency) => {
    if (currency && currency.id) {
      resetForm({
        values: {
          title: currency.titleFa || '',
          iso: currency.isoCode || '',
          selling_exchange_rate_margin: currency.sellPriceRange ?? currency.margins?.selling ?? null,
          buying_exchange_rate_margin: currency.buyPriceRange ?? currency.margins?.buying ?? null,
          auto_update_enabled: currency.auto_update_enabled ?? false,
          exchange_rate: currency.exchange_rate != null ? Number(currency.exchange_rate).toFixed(2) : null,
        },
      })
    } else {
      resetForm()
    }
  },
  { immediate: true, deep: true }
)

// --- Mutation ---
const updateCurrencyMutation = useMutationApi({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: (payload: any) => updateCurrencyAction(props.selectedCurrency.id, { ...payload }),
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// --- Auto-update toggle mutation ---
const toggleAutoUpdateMutation = useMutationApi({
  mutationFn: (autoUpdateEnabled: boolean) =>
    toggleCurrencyAutoUpdateAction(props.selectedCurrency.id, { auto_update_enabled: autoUpdateEnabled }),
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در تغییر وضعیت آپدیت خودکار')
  },
})

// --- Manual exchange rate mutation ---
const updateExchangeRateMutation = useMutationApi({
  mutationFn: (exchangeRate: number | string) =>
    updateCurrencyExchangeRateAction(props.selectedCurrency.id, { exchange_rate: exchangeRate }),
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در بروزرسانی نرخ ارز')
  },
})

const isSubmitting = () =>
  updateCurrencyMutation.isPending.value ||
  toggleAutoUpdateMutation.isPending.value ||
  updateExchangeRateMutation.isPending.value

// --- Submit ---
const onSubmit = handleSubmit(async (values) => {
  try {
    // 1) update general fields (title, iso, margins)
    await updateCurrencyMutation.mutateAsync({
      title: values.title,
      iso: values.iso,
      selling_exchange_rate_margin: values.selling_exchange_rate_margin,
      buying_exchange_rate_margin: values.buying_exchange_rate_margin,
    })

    // 2) toggle auto-update only if changed
    if (isSuperAdmin()) {
      const initialAutoUpdate = props.selectedCurrency?.auto_update_enabled ?? false
      if (values.auto_update_enabled !== initialAutoUpdate) {
        await toggleAutoUpdateMutation.mutateAsync(values.auto_update_enabled)
      }

      if (!values.auto_update_enabled && values.exchange_rate != null) {
        const initialRate = props.selectedCurrency?.exchange_rate ?? null
        if (String(values.exchange_rate) !== String(initialRate)) {
          await updateExchangeRateMutation.mutateAsync(values.exchange_rate)
        }
      }
    }

    toast.success('ارز با موفقیت ویرایش شد')
    emit('confirm')
    closeModal()
    // queryClient.invalidateQueries({ queryKey: ['currencies'] });
  } catch {
    // errors already toasted per-mutation above
  }
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="ویرایش ارز" closeDirection="left" class="update-request">

        <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
            <div class="flex-1 overflow-auto">

                <div class="grid px-4 pt-6 pb-9 gap-6">
                    <Input v-model="title" name="title" label="نام ارز" type="text" :error="errors.title" />
                    <Input v-model="iso" name="iso" label="کد ارز" type="text" :error="errors.iso" />
                    <Input v-model="selling_exchange_rate_margin" name="selling_exchange_rate_margin"
                        label="بازه قیمتی ثبت پیشنهاد روی درخواست فروش" type="number"
                        :error="errors.selling_exchange_rate_margin" />
                    <Input v-model="buying_exchange_rate_margin" name="buying_exchange_rate_margin"
                        label="بازه قیمتی ثبت پیشنهاد روی درخواست خرید" type="number"
                        :error="errors.buying_exchange_rate_margin" />
                    <div v-if="isSuperAdmin()" class="flex items-center justify-between">
                        <label class="text-sm font-medium">آپدیت خودکار نرخ ارز</label>
                        <Switch v-model="auto_update_enabled" />
                    </div>
                    <Input v-if="isSuperAdmin()" v-model="exchange_rate" name="exchange_rate" label="نرخ فعلی ارز" type="number"
                        :disabled="auto_update_enabled" :error="errors.exchange_rate" />
                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="submit" label="ویرایش" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
                    :disabled="isSubmitting()"
                    :loading="isSubmitting()" />
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" :disabled="isSubmitting()" />
            </div>
        </form>
    </Modal>
</template>
